/**
 * Rent Verification report UI — V2 (portable).
 *
 * Figma redesign: category filters, declared rent summary with 12-month status
 * grid, flat transaction table with match pills, Flinks institutions,
 * collapsible disclaimers. Download passes the active filter selection so the
 * PDF matches the modal snapshot. Kept separate from the frozen V1 modal for
 * easy production port via `verification-ui-version.ts`.
 */
import { useState } from 'react'
import type { ParsedStepData } from '#app/utils/application-detail-v2/types'
import { RENT_DISCLAIMERS } from '#app/utils/application-detail-v2/verification-disclaimers'
import {
	type RentFilterKey,
	buildRentMonthlyGrid,
	declaredRentAmount,
	defaultRentFilterSelection,
	filterRentItems,
	orderedRentFilters,
	parseConnectedFlinksAccounts,
	parseVerificationSection,
	pickAccountHolder,
	presentRentFilters,
	rentGridEndMonth,
	buildDeclaredRentPeriod,
} from '#app/utils/application-detail-v2/verification-display'
import {
	CollapsibleDisclaimers,
	FinancialInstitutionsList,
	RentCategoryFilters,
	RentSummarySection,
	RentTransactionTable,
	VerificationRedesignShell,
} from './verification-bits-v2'

function txnSortValue(value: string | null | undefined): number {
	if (!value) return Number.NEGATIVE_INFINITY
	const d = /^\d{4}-\d{2}-\d{2}$/.test(value)
		? new Date(`${value}T12:00:00Z`)
		: new Date(value)
	const t = d.getTime()
	return Number.isNaN(t) ? Number.NEGATIVE_INFINITY : t
}

export function RentVerificationModalV2({
	open,
	onOpenChange,
	aiReportData,
	rentalStep,
	verifyIncomeStep,
	showRequestBank,
	onRequestBank,
	canDownload,
	onDownload,
	downloadBusy,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	aiReportData: Record<string, unknown> | null
	rentalStep: ParsedStepData['rental']
	verifyIncomeStep: ParsedStepData['verify-income']
	showRequestBank: boolean
	onRequestBank: () => void
	canDownload: boolean
	onDownload: (filters?: RentFilterKey[]) => void
	downloadBusy: boolean
}) {
	const section = parseVerificationSection(aiReportData, 'rent')
	const items = section?.items ?? []
	const present = presentRentFilters(items)
	const initialSelected = defaultRentFilterSelection(present)

	const [prevOpen, setPrevOpen] = useState(open)
	const [selected, setSelected] = useState<RentFilterKey[]>(initialSelected)
	if (open !== prevOpen) {
		setPrevOpen(open)
		if (open) setSelected(defaultRentFilterSelection(present))
	}

	const filtered = filterRentItems(items, orderedRentFilters(selected))
	const sorted = [...filtered].sort(
		(a, b) => txnSortValue(b.date) - txnSortValue(a.date),
	)
	const holder = pickAccountHolder(items)
	const declaredRent = declaredRentAmount(rentalStep)
	const previousDeclaredRent = declaredRentAmount(rentalStep, 'previous')
	const months = buildRentMonthlyGrid(filtered, declaredRent, {
		endMonth: rentGridEndMonth(aiReportData),
	})
	const institutions = parseConnectedFlinksAccounts(verifyIncomeStep)
	const currentPeriod = buildDeclaredRentPeriod(rentalStep, 'current')
	const previousPeriod = buildDeclaredRentPeriod(rentalStep, 'previous')

	return (
		<VerificationRedesignShell
			open={open}
			onOpenChange={onOpenChange}
			title="Rent Verification"
			accountHolder={holder}
			showRequestBank={showRequestBank}
			onRequestBank={onRequestBank}
			canDownload={canDownload}
			onDownload={() => onDownload(orderedRentFilters(selected))}
			downloadBusy={downloadBusy}
			filters={
				<RentCategoryFilters
					present={present}
					selected={selected}
					onChange={setSelected}
				/>
			}
		>
			<RentSummarySection
				declaredRent={declaredRent}
				previousDeclaredRent={previousDeclaredRent}
				months={months}
				currentPeriod={currentPeriod}
				previousPeriod={previousPeriod}
			/>

			<RentTransactionTable items={sorted} totalCount={items.length} />

			<FinancialInstitutionsList accounts={institutions} />

			<CollapsibleDisclaimers disclaimers={RENT_DISCLAIMERS} />
		</VerificationRedesignShell>
	)
}
